import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDeviceSizeListener } from '../../hooks/common/mobile/useDeviceSizeListener';
import { selectAccountId } from '../../redux/reducers/account/account';
import { accountRoutes, searchRoutes } from '../../constants/routes';
import { addLangPrefix } from '../common/buttons/Link';
import Icon, { iconNames } from '../common/icon';
import { COLORS } from '../../constants';

const LANG = '(?:[a-z]{2}\\/)?';
const HIDE_ON_PATTERNS = [
    new RegExp(`^\\/${LANG}add-advert`),
    new RegExp(`^\\/${LANG}sell\\/(car|bike|commercial)(\\/[^/]+)?$`),
    new RegExp(`^\\/${LANG}sell\\/all\\/(car|bike|commercial)\\/[^/]+`),
    new RegExp(`^\\/${LANG}sell\\/pro\\/`),
    new RegExp(`^\\/${LANG}parts\\/`),
    new RegExp(`^\\/${LANG}placement\\/`),
    new RegExp(`^\\/${LANG}region-service\\/`),
    new RegExp(`^\\/${LANG}rent\\/`),
    new RegExp(`^\\/${LANG}company\\/`),
    new RegExp(`^\\/${LANG}checkauto\\/`),
    new RegExp(`^\\/${LANG}search\\/`),
    new RegExp(`^\\/${LANG}account\\/(login|signup|forgot|new|verificate)`),
    new RegExp(`^\\/${LANG}account\\/[^/]+\\/tops\\/`),
];

const TYPE_MATCH =
    /\/(?:search(?:\/full)?|account\/(?:favorites|garage))\/(car|bike|commercial)/;
const DEFAULT_TYPE = 'car';

const searchRouteFor = (type) =>
    searchRoutes[type.toUpperCase()] || searchRoutes.CAR;

const favoriteRouteFor = (type) =>
    accountRoutes[`FAVORITE_${type.toUpperCase()}`] || accountRoutes.FAVORITE_CAR;

const BottomNav = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const accountId = useSelector(selectAccountId);
    const { isMobile } = useDeviceSizeListener({ breakpoint: 768 });

    if (!isMobile) return null;
    if (HIDE_ON_PATTERNS.some((re) => re.test(pathname))) return null;

    const lang = i18n.language || 'ge';
    const activeType = pathname.match(TYPE_MATCH)?.[1] || DEFAULT_TYPE;
    const goTo = (path) => navigate(addLangPrefix(path, lang));

    const items = [
        {
            key: 'home',
            label: t('home'),
            icon: iconNames.list,
            onClick: () => navigate(`/${lang}/`),
            active: pathname === `/${lang}` || pathname === `/${lang}/`,
        },
        {
            key: 'search',
            label: t('search'),
            icon: iconNames.search,
            onClick: () => goTo(searchRouteFor(activeType)),
            active: /\/search\//.test(pathname),
        },
        {
            key: 'add',
            label: t('addAdvert'),
            icon: iconNames.plusIcon,
            onClick: () => goTo('/add-advert'),
            active: /\/add-advert/.test(pathname),
            primary: true,
        },
        {
            key: 'favorites',
            label: t('favorites'),
            icon: iconNames.starEmpty,
            onClick: () => goTo(favoriteRouteFor(activeType)),
            active: /\/account\/favorites\//.test(pathname),
        },
        {
            key: 'account',
            label: accountId ? t('cabinet') : t('enter'),
            icon: iconNames.user,
            onClick: () =>
                goTo(accountId ? accountRoutes.CABINET : accountRoutes.LOGIN),
            active: /\/account\/(cabinet|login)/.test(pathname),
        },
    ];

    return (
        <nav className="bottom-nav" aria-label="Bottom navigation">
            {items.map((item) => (
                <button
                    key={item.key}
                    type="button"
                    onClick={item.onClick}
                    className={`bottom-nav__item${item.active ? ' bottom-nav__item--active' : ''}${item.primary ? ' bottom-nav__item--primary' : ''}`}
                    aria-label={item.label}
                >
                    <span className="bottom-nav__icon">
                        <Icon
                            name={item.icon}
                            size={item.primary ? 20 : 22}
                            fill={
                                item.primary
                                    ? COLORS.WHITE
                                    : item.active
                                      ? COLORS.YELLOW
                                      : COLORS.GRAY_LIGHT
                            }
                        />
                    </span>
                    <span className="bottom-nav__label">{item.label}</span>
                </button>
            ))}
        </nav>
    );
};

export default BottomNav;
